/* v8 ignore start */
import {JsonModel} from "@/lib/core/jsonModel";
import {LottieTextCaps} from "@/lib/lottie/constants/lottieTextCaps";
import {LottieTextJustify} from "@/lib/lottie/constants/lottieTextJustify";
import {TLottieColor} from "@/lib/lottie/helpers/lottieColor";
import {TRGB} from "@/lib/lottie/layers/lottieTextLayerModel";
import {ErrorUtils} from "@/lib/utils/error/errorUtils";
import {TextUtils} from "@/lib/utils/textUtils";

const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 150;

const LINE_HEIGHT_COEFFICIENT = 1.2;
const FONT_SIZE_STEP = 0.5;
const FONT_NAME = "Proxima Nova";

export class LottieTextDocumentModel extends JsonModel<LottieTextDocumentModel.IJson> {
  public get text(): string {
    return this.json.t;
  }

  public get fontSize(): number {
    return this.json.s ?? ErrorUtils.throwMessage("missing fontSize");
  }

  public get lineHeight(): number {
    return this.json.lh ?? ErrorUtils.throwMessage("missing lineHeight");
  }

  public get textHeight(): number {
    const lines = this.text.split("\r");

    return lines.length * this.lineHeight;
  }

  public get justify(): LottieTextJustify {
    return this.json.j ?? ErrorUtils.throwMessage("missing justify");
  }

  public changeColor(color: TRGB) {
    return this.update({fc: color});
  }

  public replaceTextWithAutoFit(newText: string, maxWidth: number, maxHeight: number) {
    const originalFontSize = this.json.s;

    const {fontSize: newFontSize, lines: wrappedText} = this.calculateNewFontSize(
      originalFontSize,
      newText,
      maxWidth,
      maxHeight
    );

    return this.update({
      t: wrappedText.join("\r"),
      s: newFontSize,
      lh: newFontSize * LINE_HEIGHT_COEFFICIENT,
    });
  }

  private calculateNewFontSize(originalFontSize: number, newText: string, maxWidth: number, maxHeight: number) {
    let fontSize = Math.min(Math.max(originalFontSize, MIN_FONT_SIZE), MAX_FONT_SIZE);
    let lines;
    let totalHeight;
    let maxLineWidth;

    do {
      lines = this.wrapText(newText, maxWidth, fontSize);
      totalHeight = lines.length * fontSize * LINE_HEIGHT_COEFFICIENT;
      fontSize -= FONT_SIZE_STEP;

      fontSize = Math.max(fontSize - FONT_SIZE_STEP, MIN_FONT_SIZE);
      maxLineWidth = Math.max(...lines.map((line) => new TextUtils().estimateTextWidth(line, FONT_NAME, fontSize)));

      if (maxLineWidth > maxWidth || totalHeight > maxHeight) {
        fontSize = Math.max(fontSize - FONT_SIZE_STEP, MIN_FONT_SIZE);
      } else {
        break;
      }
    } while (fontSize > MIN_FONT_SIZE);

    return {fontSize, lines, totalHeight, maxLineWidth};
  }

  private wrapText(text: string, maxWidth: number, fontSize: number) {
    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = new TextUtils().estimateTextWidth(currentLine + " " + word, FONT_NAME, fontSize);

      if (width < maxWidth) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    lines.push(currentLine);

    return lines;
  }
}

export namespace LottieTextDocumentModel {
  export interface IJson {
    /**
     * Font Family
     * @default ""
     */
    f: string;

    /**
     * Fill Color
     * @default [0, 0, 0]
     */
    fc: TLottieColor;

    /**
     * Stroke Color
     */
    sc?: TLottieColor;

    /**
     * Stroke Width
     * @default 0
     */
    sw?: number;

    /**
     * Stroke Over Fill
     * Render stroke above the fill
     */
    of?: boolean;

    /**
     * Font Size
     * @default 10
     */
    s: number;

    /**
     * Line Height
     * Distance between lines on multiline or wrapped text
     */
    lh?: number;

    /**
     * Wrap Size
     * Size of the box containing the text
     */
    sz?: [number, number];

    /**
     * Wrap Position
     * Position of the box containing the text
     */
    ps?: [number, number];

    /**
     * Text
     * Text, note that newlines are encoded with \r
     * @default ""
     */
    t: string;

    /**
     * Justify
     * @default 0
     */
    j?: LottieTextJustify;

    /**
     * Text Caps
     */
    ca?: LottieTextCaps;

    /**
     * Tracking
     * Text Tracking
     */
    tr?: number;

    /**
     * Baseline Shift
     */
    ls?: number;
  }
}
/* v8 ignore stop */
